import dayjs from "dayjs";
import services from "../services";
import store from "../store";

const strict = true;

const defaultState = function () {
  return {
    availableGuides: []
  };
};

const state = defaultState();

const getters = {
  getAvailableGuides: state => state.availableGuides
};

const mutations = {
  reset(state) {
    const s = defaultState();
    Object.keys(s).forEach(key => {
      state[key] = s[key];
    });
  },
  setAvailableGuides(state, guides) {
    state.availableGuides = guides;
  }
};

const actions = {
  async initGuides({ commit, rootGetters }) {
    let mhGuide = rootGetters["login/getMhGuide"];
    let isConiuge = rootGetters["profile/getIsConiuge"];
    let hasNucleoFamiliare = rootGetters["login/getNucleoFamiliare"];
    let hasEmvaCoverage = rootGetters["coverages/getHasEmvaCoverage"];
    let currentYear = dayjs().year();
    // Guide MutualHelp
    let mhGuidesITA = buildGuides(buildGuidaMHIta, currentYear, 2023);
    let mhGuidesDE = buildGuides(buildGuidaMHDe, currentYear, 2023);
    // Guide UniSalute
    let usGuides = buildGuides(buildGuidaUS, currentYear, 2023);
    // Guide FondoEst o guide EMVA (sono esclusive)
    let feGuides = [];
    let emvaGuidesITA = [];
    let emvaGuidesDE = [];
    if (hasEmvaCoverage) {
      // guide EMVA in base a come è configurato l'iscritto autenticato
      if (isConiuge) {
        emvaGuidesITA = buildGuides(buildGuidaEMVAConIta, currentYear, 2026);
        emvaGuidesDE = buildGuides(buildGuidaEMVAConDe, currentYear, 2026);
      } else {
        emvaGuidesITA = buildGuides(buildGuidaEMVAIta, currentYear, 2026);
        emvaGuidesDE = buildGuides(buildGuidaEMVADe, currentYear, 2026);
      }
      if (hasNucleoFamiliare) {
        emvaGuidesITA = emvaGuidesITA.concat(
          buildGuides(buildGuidaEMVAFamIta, currentYear, 2026)
        );
        emvaGuidesDE = emvaGuidesDE.concat(
          buildGuides(buildGuidaEMVAFamDe, currentYear, 2026)
        );
      }
    } else {
      // guide FondoEst in base a come è configurato l'iscritto autenticato
      if (isConiuge) {
        feGuides = buildGuides(buildGuidaFECon, currentYear, 2026);
      } else {
        feGuides = buildGuides(buildGuidaFE, currentYear, 2023);
      }
      if (hasNucleoFamiliare) {
        feGuides = feGuides.concat(
          buildGuides(buildGuidaFEFam, currentYear, 2026)
        );
      }
    }
    // Build final available guides
    let availableGuides = [];
    if (mhGuide === "A") {
      // guide MutualHelp + guide UniSalute
      availableGuides = mhGuidesITA.concat(mhGuidesDE).concat(usGuides);
    }
    if (mhGuide === "B") {
      // solo guide MutualHelp
      availableGuides = mhGuidesITA.concat(mhGuidesDE);
    }
    if (mhGuide === "C") {
      // (guide FondoEst o guide EMVA) + guide UniSalute
      availableGuides = feGuides
        .concat(emvaGuidesITA)
        .concat(emvaGuidesDE)
        .concat(usGuides);
    }
    commit("setAvailableGuides", availableGuides);
  },
  async downloadGuidePDFFile({ commit }, documentType) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let docXML = await services.fondoEstPerTeService.callDownloadGuidePDFFile(
        {
          docType: documentType
        }
      );
      let blob = new Blob([docXML.data], { type: "application/pdf" });
      let y = documentType.slice(-4); // anno della guida
      let dt = documentType.replaceAll(y, ""); // tipologia della guida
      let types = {
        FEITA: `Guida Fondo Est ${y}.pdf`,
        USITA: `Guida UniSalute ${y}.pdf`,
        MHITA: `Guida Bolzano Mutual Help ${y} ITA.pdf`,
        MHDE: `Guida Bolzano Mutual Help ${y} DE.pdf`,
        FAMITA: `Guida Familiari ${y}.pdf`,
        EMVAITA: `Guida EMVA ${y} ITA.pdf`,
        EMVAFAMITA: `Guida EMVA Familiari ${y} ITA.pdf`,
        EMVADE: `Guida EMVA ${y} DE.pdf`,
        EMVAFAMDE: `Guida EMVA Familiari ${y} DE.pdf`
      };
      let file = window.URL.createObjectURL(blob, {
        type: "application/pdf"
      });
      let a = document.createElement("a");
      a.href = file;
      a.download = types[dt] ? types[dt] : documentType + ".pdf";
      document.body.appendChild(a);
      a.click();
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  }
};

function buildGuides(builder, currentYear, introducedYear) {
  let guides = [];
  let minYear = Math.max(currentYear - 2, introducedYear);
  for (let year = currentYear; year >= minYear; year--) {
    guides.push(builder(year.toString()));
  }
  return guides;
}

// Guida UniSalute
function buildGuidaUS(year) {
  let g = {};
  g["id"] = `USITA${year}`;
  g["name"] = `Guida UniSalute ${year}.pdf`;
  g[
    "text"
  ] = `Guida ${year} alle prestazioni sanitarie erogate attraverso UniSalute`;
  g["year"] = year;
  return g;
}

// Guida FondoEst
function buildGuidaFE(year) {
  let g = {};
  g["id"] = `FEITA${year}`;
  g["name"] = `Guida Fondo Est ${year}.pdf`;
  g["text"] = `Guida ${year} alle prestazioni sanitarie`;
  g["year"] = year;
  return g;
}

// Guida FondoEst per i familiari (figli minorenni)
function buildGuidaFEFam(year) {
  let g = {};
  g["id"] = `FAMITA${year}`;
  g["name"] = `Guida Familiari ${year}.pdf`;
  g["text"] = `Guida ${year} alle prestazioni sanitarie per i familiari`;
  g["year"] = year;
  return g;
}

// Guida FondoEst per i coniugi
function buildGuidaFECon(year) {
  let g = {};
  g["id"] = `CONITA${year}`;
  g["name"] = `Guida Coniugi ${year}.pdf`;
  g[
    "text"
  ] = `Guida ${year} alle prestazioni sanitarie per i coniugi (matrimonio, unioni civili) e conviventi di fatto fiscalmente a carico degli iscritti al Fondo Est`;
  g["year"] = year;
  return g;
}

// Guida EMVA in italiano
function buildGuidaEMVAIta(year) {
  let g = {};
  g["id"] = `EMVAITA${year}`;
  g["name"] = `Guida EMVA ${year} ITA.pdf`;
  g[
    "text"
  ] = `Guida ${year} in italiano alle prestazioni sanitarie per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

// Guida EMVA in italiano per i familiari (figli minorenni)
function buildGuidaEMVAFamIta(year) {
  let g = {};
  g["id"] = `EMVAFAMITA${year}`;
  g["name"] = `Guida EMVA Familiari ${year} ITA.pdf`;
  g[
    "text"
  ] = `Guida ${year} in italiano alle prestazioni sanitarie per i figli minorenni degli iscritti al Fondo Est per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

// Guida EMVA in italiano per i coniugi
function buildGuidaEMVAConIta(year) {
  let g = {};
  g["id"] = `EMVACONITA${year}`;
  g["name"] = `Guida EMVA Coniugi ${year} ITA.pdf`;
  g[
    "text"
  ] = `Guida ${year} in italiano alle prestazioni sanitarie per i coniugi (matrimonio, unioni civili) e conviventi di fatto fiscalmente a carico degli iscritti al Fondo Est per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

// Guida EMVA in tedesco
function buildGuidaEMVADe(year) {
  let g = {};
  g["id"] = `EMVADE${year}`;
  g["name"] = `Guida EMVA ${year} DE.pdf`;
  g[
    "text"
  ] = `Guida ${year} in tedesco alle prestazioni sanitarie per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

// Guida EMVA in tedesco per i familiari (figli minorenni)
function buildGuidaEMVAFamDe(year) {
  let g = {};
  g["id"] = `EMVAFAMDE${year}`;
  g["name"] = `Guida EMVA Familiari ${year} DE.pdf`;
  g[
    "text"
  ] = `Guida ${year} in tedesco alle prestazioni sanitarie per i figli minorenni degli iscritti al Fondo Est per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

// Guida EMVA in tedesco per i coniugi
function buildGuidaEMVAConDe(year) {
  let g = {};
  g["id"] = `EMVACONDE${year}`;
  g["name"] = `Guida EMVA Coniugi ${year} DE.pdf`;
  g[
    "text"
  ] = `Guida ${year} in tedesco alle prestazioni sanitarie per i coniugi (matrimonio, unioni civili) e conviventi di fatto fiscalmente a carico degli iscritti al Fondo Est per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

function buildGuidaMHIta(year) {
  let g = {};
  g["id"] = `MHITA${year}`;
  g["name"] = `Guida Bolzano Mutual Help ${year} ITA.pdf`;
  g[
    "text"
  ] = `Guida ${year} in italiano alle prestazioni sanitarie per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

function buildGuidaMHDe(year) {
  let g = {};
  g["id"] = `MHDE${year}`;
  g["name"] = `Guida Bolzano Mutual Help ${year} DE.pdf`;
  g[
    "text"
  ] = `Guida ${year} in tedesco alle prestazioni sanitarie per i dipendenti di aziende con sede operativa nella provincia di Bolzano`;
  g["year"] = year;
  return g;
}

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
